/**
 * FreeResponseScreen.js
 * ======================
 * Gaze-driven screen for nodes marked isFreeResponse (e.g. "Turn on TV",
 * "Get My Phone").  Shows up to 3 custom phrases as gaze-selectable blocks;
 * selecting one speaks it aloud via the Web Speech API.
 *
 * Caregivers manage the phrase list by tapping the [+] button in the BACK bar,
 * which opens a panel where phrases can be added, deleted, and reordered.
 * Options are stored in GazeConfigContext and persist for the session.
 *
 * Gaze mapping
 * ─────────────
 *  up    → navigate back
 *  left  → phrase[0]
 *  right → phrase[1]
 *  down  → phrase[2]
 *
 * Font sizes auto-fit to the block height using a measurement loop.
 */

import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getNode } from './responseData';
import { useGazeNavigation } from './useGazeNavigation';
import { useGazeConfig, COLOR_MAP } from './GazeConfigContext';
import { speakSegments } from './speakUtils';
import './QuadrantPage.css';
import './FreeResponseScreen.css';

const LONG_GAZE_FEEDBACK_MS = 400;
const CONFIRM_TIMEOUT_MS    = 45_000;

// ── Speech helper ─────────────────────────────────────────────────────────────

function speak(text, voice) {
  if (!window.speechSynthesis) return;
  window.speechSynthesis.cancel();
  const utt = new SpeechSynthesisUtterance(text);
  utt.rate   = 0.95;
  utt.pitch  = 1.0;
  utt.volume = 1.0;
  if (voice) utt.voice = voice;
  window.speechSynthesis.speak(utt);
}

// ── Auto-fit label ────────────────────────────────────────────────────────────
// Renders text at up to 34px, stepping down until it fits the block height.

function OptionLabel({ text }) {
  const wrapRef  = useRef(null);
  const labelRef = useRef(null);
  const [fontSize, setFontSize] = useState(estimateSize(text));

  useEffect(() => {
    const wrap  = wrapRef.current;
    const label = labelRef.current;
    if (!wrap || !label) return;
    const maxH = wrap.clientHeight - 24;
    for (let size = 34; size >= 12; size -= 2) {
      label.style.fontSize = size + 'px';
      if (label.getBoundingClientRect().height <= maxH) {
        setFontSize(size);
        return;
      }
    }
    setFontSize(12);
  }, [text]);

  return (
    <div ref={wrapRef} className="frs-label-wrap">
      <span ref={labelRef} className="quad-label" style={{ fontSize }}>
        {text}
      </span>
    </div>
  );
}

function estimateSize(text) {
  const len = (text || '').length;
  if (len <= 8)  return 34;
  if (len <= 15) return 26;
  if (len <= 25) return 20;
  if (len <= 40) return 16;
  return 13;
}

// ── Main component ────────────────────────────────────────────────────────────

export default function FreeResponseScreen() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const nodeId   = String(id);
  const node     = getNode(Number(id));

  const {
    config, windowSize, calibration,
    freeResponseOptions, addFreeResponseOption, removeFreeResponseOption, moveFreeResponseOption,
  } = useGazeConfig();

  const isLongGaze = config.confirmationMode === 'long-gaze';

  const colorVars = {
    '--q-bg':   COLOR_MAP[config.backgroundColor] ?? '#ffffff',
    '--q-text': COLOR_MAP[config.textColor]        ?? '#000000',
  };

  const options = freeResponseOptions[nodeId] ?? [];
  const [opt0, opt1, opt2] = options;   // first 3 are gaze-accessible

  // ── Selection state ───────────────────────────────────────────────────────
  const [spokenIdx,  setSpokenIdx]  = useState(null); // last confirmed option
  const [pendingIdx, setPendingIdx] = useState(null); // highlighted, awaiting confirm

  const pendingIdxRef  = useRef(null);
  const pendingTimerRef = useRef(null);

  useEffect(() => { pendingIdxRef.current = pendingIdx; }, [pendingIdx]);
  useEffect(() => () => clearTimeout(pendingTimerRef.current), []);

  // ── On-demand read aloud ──────────────────────────────────────────────────
  function speakPage() {
    speakSegments([
      opt0 ? ['Look left for',  opt0] : null,
      opt1 ? ['Look right for', opt1] : null,
      opt2 ? ['Look down for',  opt2] : null,
      ['Look up to go back'],
    ].filter(Boolean), config.voiceURI);
  }

  // ── Manage-panel state ───────────────────────────────────────────────────
  const [showPanel,      setShowPanel]      = useState(false);
  const [newText,        setNewText]        = useState('');
  const addInputRef = useRef(null);

  // Focus the add input when the panel opens
  useEffect(() => {
    if (showPanel) setTimeout(() => addInputRef.current?.focus(), 50);
  }, [showPanel]);

  // ── Helpers ──────────────────────────────────────────────────────────────

  function clearPending() {
    setPendingIdx(null);
    pendingIdxRef.current = null;
    if (pendingTimerRef.current) {
      clearTimeout(pendingTimerRef.current);
      pendingTimerRef.current = null;
    }
  }

  function confirmOption(idx) {
    const text = options[idx];
    if (!text) return;
    const voice = config.voiceURI
      ? (window.speechSynthesis?.getVoices().find(v => v.voiceURI === config.voiceURI) ?? null)
      : null;
    speak(text, voice);
    setSpokenIdx(idx);
    clearPending();
  }

  function handleGazeSelect(idx) {
    if (pendingIdxRef.current !== null || !options[idx]) return;
    setSpokenIdx(null);
    setPendingIdx(idx);
    pendingIdxRef.current = idx;

    if (isLongGaze) {
      pendingTimerRef.current = setTimeout(() => confirmOption(idx), LONG_GAZE_FEEDBACK_MS);
    } else {
      pendingTimerRef.current = setTimeout(clearPending, CONFIRM_TIMEOUT_MS);
    }
  }

  // ── Gaze navigation ───────────────────────────────────────────────────────
  const { videoRef, activeDirection, doubleBlinkCount } = useGazeNavigation({
    enabled: !showPanel,
    windowSize,
    calibration,
    onGazeSelect: (direction) => {
      if (!isLongGaze && pendingIdxRef.current !== null) {
        if (direction === 'up') clearPending();
        return;
      }
      if (direction === 'up')    { navigate(-1); return; }
      if (direction === 'left')  handleGazeSelect(0);
      if (direction === 'right') handleGazeSelect(1);
      if (direction === 'down')  handleGazeSelect(2);
    },
  });

  // Double-blink confirmation
  useEffect(() => {
    if (doubleBlinkCount === 0) return;
    const idx = pendingIdxRef.current;
    if (idx === null) return;
    confirmOption(idx);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doubleBlinkCount]);

  // ── CSS helpers ───────────────────────────────────────────────────────────

  function blockClass(idx) {
    if (idx === spokenIdx)  return 'quad-selected';
    if (idx === pendingIdx) return 'quad-selected';
    if (pendingIdx !== null || spokenIdx !== null) return 'quad-dimmed';
    return '';
  }

  function gazeClass(dir) {
    if (!isLongGaze && pendingIdx !== null) return '';
    return activeDirection === dir ? 'quad-active' : '';
  }

  // ── Manage-panel handlers ─────────────────────────────────────────────────

  function handleAdd() {
    const text = newText.trim();
    if (!text) return;
    addFreeResponseOption(nodeId, text);
    setNewText('');
    addInputRef.current?.focus();
  }

  const pageTitle = node
    ? node.label.replace('\n', ' ').toUpperCase()
    : 'CUSTOM RESPONSES';

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="quad-page" style={colorVars}>
      <video ref={videoRef} playsInline muted
        style={{ position: 'absolute', width: 0, height: 0, opacity: 0 }} />

      <div className="quad-card">

        {/* ── BACK bar with speak + manage [+] buttons ────────────────────── */}
        <div className={`quad-top quad-back frs-back-bar ${gazeClass('up')}`}
          onClick={() => navigate(-1)}>
          <span className="quad-label">← BACK</span>
          <button
            className="quad-speak-btn"
            onClick={e => { e.stopPropagation(); speakPage(); }}
            aria-label="Read options aloud"
          >
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
              <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
              <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
            </svg>
          </button>
          <button
            className="frs-manage-btn"
            onClick={e => { e.stopPropagation(); setShowPanel(true); }}
            aria-label="Manage custom responses"
          >
            <svg viewBox="0 0 16 16" width="20" height="20" fill="currentColor" aria-hidden="true">
              <path d="M8 1.5a.5.5 0 0 1 .5.5v5h5a.5.5 0 0 1 0 1h-5v5a.5.5 0 0 1-1 0v-5h-5a.5.5 0 0 1 0-1h5v-5a.5.5 0 0 1 .5-.5"/>
            </svg>
          </button>
        </div>

        {/* ── Middle row: option 0 (left) + option 1 (right) ──────────────── */}
        <div className="quad-middle">
          <div
            className={`quad-left ${opt0 ? blockClass(0) : 'frs-empty'} ${opt0 ? gazeClass('left') : ''}`}
            onClick={() => handleGazeSelect(0)}
          >
            {opt0
              ? <OptionLabel text={opt0} />
              : <span className="frs-placeholder">Tap + to add</span>
            }
          </div>
          <div
            className={`quad-right ${opt1 ? blockClass(1) : 'frs-empty'} ${opt1 ? gazeClass('right') : ''}`}
            onClick={() => handleGazeSelect(1)}
          >
            {opt1
              ? <OptionLabel text={opt1} />
              : <span className="frs-placeholder">Tap + to add</span>
            }
          </div>
        </div>

        {/* ── Bottom: option 2 ─────────────────────────────────────────────── */}
        <div
          className={`quad-bottom ${opt2 ? blockClass(2) : 'frs-empty'} ${opt2 ? gazeClass('down') : ''}`}
          onClick={() => handleGazeSelect(2)}
        >
          {opt2
            ? <OptionLabel text={opt2} />
            : <span className="frs-placeholder">Tap + to add</span>
          }
        </div>

      </div>

      {/* ── Manage-options panel ──────────────────────────────────────────── */}
      {showPanel && (
        <div className="frs-overlay" onClick={() => setShowPanel(false)}>
          <div className="frs-panel" onClick={e => e.stopPropagation()}>

            <div className="frs-panel-header">
              <h2 className="frs-panel-title">{pageTitle}</h2>
              <button className="frs-panel-close" onClick={() => setShowPanel(false)} aria-label="Close">
                <svg viewBox="0 0 16 16" width="16" height="16" fill="currentColor" aria-hidden="true">
                  <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8z"/>
                </svg>
              </button>
            </div>

            <p className="frs-panel-hint">
              First 3 options are gaze-selectable. Use arrows to reorder.
            </p>

            <ul className="frs-option-list">
              {options.length === 0 && (
                <li className="frs-option-empty">No options yet — add one below.</li>
              )}
              {options.map((opt, i) => (
                <li key={i} className={`frs-option-item ${i < 3 ? 'frs-option-item--gaze' : ''}`}>
                  <span className="frs-gaze-badge" aria-label={i < 3 ? 'Gaze-selectable' : ''}>
                    {i < 3 ? i + 1 : '·'}
                  </span>
                  <span className="frs-option-text">{opt}</span>
                  <div className="frs-option-actions">
                    <button
                      className="frs-arrow-btn"
                      onClick={() => moveFreeResponseOption(nodeId, i, i - 1)}
                      disabled={i === 0}
                      aria-label="Move up"
                    >▲</button>
                    <button
                      className="frs-arrow-btn"
                      onClick={() => moveFreeResponseOption(nodeId, i, i + 1)}
                      disabled={i === options.length - 1}
                      aria-label="Move down"
                    >▼</button>
                    <button
                      className="frs-delete-btn"
                      onClick={() => removeFreeResponseOption(nodeId, i)}
                      aria-label={`Delete: ${opt}`}
                    >
                      <svg viewBox="0 0 16 16" width="14" height="14" fill="currentColor" aria-hidden="true">
                        <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8z"/>
                      </svg>
                    </button>
                  </div>
                </li>
              ))}
            </ul>

            <div className="frs-add-row">
              <input
                ref={addInputRef}
                className="frs-add-input"
                type="text"
                value={newText}
                onChange={e => setNewText(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleAdd(); }}
                placeholder="Type a response phrase…"
                maxLength={100}
              />
              <button
                className="frs-add-btn"
                onClick={handleAdd}
                disabled={!newText.trim()}
              >
                Add
              </button>
            </div>

          </div>
        </div>
      )}
    </div>
  );
}
